/**
 * Utilities for comparing spoken text against expected words/phrases.
 * Used by Spelling Practice and Karaoke Practice modes.
 */

/** Normalize text for comparison: lowercase, strip punctuation, collapse whitespace. */
export function normalizeForComparison(text: string): string {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // strip diacritics
    .replace(/[^\p{L}\p{N}\s]/gu, "") // keep letters, numbers, spaces
    .replace(/\s+/g, " ")
    .trim();
}

/** Levenshtein edit distance between two strings. */
export function levenshteinDistance(a: string, b: string): number {
  const m = a.length;
  const n = b.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));

  for (let i = 0; i <= m; i++) dp[i]![0] = i;
  for (let j = 0; j <= n; j++) dp[0]![j] = j;

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i]![j] =
        a[i - 1] === b[j - 1]
          ? dp[i - 1]![j - 1]!
          : 1 + Math.min(dp[i - 1]![j]!, dp[i]![j - 1]!, dp[i - 1]![j - 1]!);
    }
  }

  return dp[m]![n]!;
}

/** Similarity ratio (0-1) between two strings based on Levenshtein distance. */
export function similarity(a: string, b: string): number {
  const maxLen = Math.max(a.length, b.length);
  if (maxLen === 0) return 1;
  return 1 - levenshteinDistance(a, b) / maxLen;
}

/** Check if a spoken word matches the expected word (similarity >= threshold). */
export function matchSpokenWord(
  expected: string,
  spoken: string,
  threshold = 0.7
): boolean {
  const normExpected = normalizeForComparison(expected);
  const normSpoken = normalizeForComparison(spoken);
  return similarity(normExpected, normSpoken) >= threshold;
}

export interface PhraseMatchResult {
  word: string;
  matched: boolean;
}

/**
 * Match spoken phrase against expected phrase, word by word.
 * Uses greedy left-to-right matching with fuzzy similarity.
 */
export function matchPhraseWords(
  expectedPhrase: string,
  spokenPhrase: string,
  threshold = 0.7
): PhraseMatchResult[] {
  const expectedWords = expectedPhrase.split(/\s+/).filter(Boolean);
  const spokenWords = normalizeForComparison(spokenPhrase)
    .split(/\s+/)
    .filter(Boolean);

  let spokenIdx = 0;

  return expectedWords.map((word) => {
    const normWord = normalizeForComparison(word);

    // Try to match against remaining spoken words
    if (spokenIdx < spokenWords.length) {
      if (similarity(normWord, spokenWords[spokenIdx]!) >= threshold) {
        spokenIdx++;
        return { word, matched: true };
      }
      // Look ahead one word in case of insertion
      if (
        spokenIdx + 1 < spokenWords.length &&
        similarity(normWord, spokenWords[spokenIdx + 1]!) >= threshold
      ) {
        spokenIdx += 2;
        return { word, matched: true };
      }
    }

    return { word, matched: false };
  });
}
